import { Group } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { useAgentEventLogs, useAgentEventRelatedTask } from '../../../api/events-client/events-client.hooks';
import { AgentEventLog } from '../../../api/events-client/events-client.type';
import { ContentPlaceholder } from '../../../components/content-placeholder/content-placeholder';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { baseWhite } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { H2 } from '../../../ui-library/typography/typography';
import { DashboardInfoCard } from '../../dashboards/components/dashboard-info-card';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { TaskDetail } from '../../work-periods/task-detail/task-detail';
import { calculateAgentEventMetadata } from '../agentic-process-helpers';
import { AgentEventSidePanel } from './agent-event-side-panel';
import { AgentGraph } from './agent-graph';
import { AgenticProcessGraphOverview } from './agentic-process-graph-overview';

export const AgenticProcessGraph = () => {
  useDocumentTitle('Agentic Process Graph - Bloomfilter');

  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('taskId');
  const sessionId = searchParams.get('sessionId');
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState<AgentEventLog | null>(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);

  // Determine if we're viewing by taskId or sessionId
  const isTaskView = !!taskId;
  const isSessionView = !isTaskView && !!sessionId;

  // Get task data only if we have a taskId
  const { data: task, query: taskQuery } = useAgentEventRelatedTask(taskId || '');

  // Get agent event logs based on either taskId or sessionId
  const filters = isTaskView ? { taskId: taskId } : isSessionView ? { sessionId: sessionId } : {};
  const { data: agentEventLogs, query: agentEventLogsQuery } = useAgentEventLogs(filters, isTaskView || isSessionView);

  const metadata = useMemo(() => calculateAgentEventMetadata(agentEventLogs || []), [agentEventLogs]);

  const getHeaderTitle = () => {
    if (isTaskView && task) {
      const taskTitle = task.title || task.name || '';
      return `${t('navigation.agentic_process')} Map - ${taskTitle}`;
    }
    if (isSessionView) {
      return `${t('navigation.agentic_process')} Map - Session`;
    }
    return `${t('navigation.agentic_process')} Map`;
  };

  const header = (
    <Group gap={5}>
      <Button
        radius="xl"
        variant="secondary"
        onClick={() => {
          navigate('/application/agentic-process');
        }}
        leftSection={<Icon name="keyboard_backspace" color={baseWhite} style={{ cursor: 'pointer' }} />}
        style={{ marginRight: 8 }}
      >
        {t('common.actions.back')}
      </Button>
      <H2 style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
        {getHeaderTitle()}
      </H2>
    </Group>
  );

  const isLoading = (isTaskView ? taskQuery.isLoading : false) || agentEventLogsQuery.isLoading;
  const hasError = (isTaskView && !taskQuery.isLoading && !task && !!taskId) || Boolean(agentEventLogsQuery.isError);
  const canRender = isTaskView ? Boolean(task) : Boolean(isSessionView);

  return (
    <MenuDrivenContainer header={header}>
      <BrandedLoadingOverlay visible={isLoading} variant="colored" />
      {hasError && (
        <DashboardInfoCard
          title={isTaskView ? `${t('common.work_unit')} not found` : 'Session not found'}
          objectName={isTaskView ? t('common.work_unit') : 'Session'}
          description={
            isTaskView
              ? `Try changing your global ${t('common.portfolio').toLowerCase()} or select a different ${t('common.work_unit').toLowerCase()}.`
              : 'No agent events found for this session.'
          }
        />
      )}
      {canRender && (
        <PageLayout
          controlBar={undefined}
          overview={{
            content: <AgenticProcessGraphOverview metadata={metadata} />,
            collapsible: true,
            title: 'Overview',
          }}
          sideFilters={
            isTaskView && task
              ? {
                  title: t('common.work_unit'),
                  show: showTaskDetail,
                  setShow: setShowTaskDetail,
                  content: <TaskDetail task={task} onClose={() => setShowTaskDetail(false)} />,
                }
              : undefined
          }
          mainSection={
            agentEventLogs && agentEventLogs.length > 0 ? (
              <AgentGraph
                task={task}
                sessionId={sessionId}
                agentEvents={agentEventLogs}
                setSelectedEvent={setSelectedEvent}
                selectedEvent={selectedEvent}
              />
            ) : (
              <ContentPlaceholder icon="assignment" text={`No agent events found.`} />
            )
          }
          sideSection={
            selectedEvent && (
              <AgentEventSidePanel
                task={task || undefined}
                agentEvent={selectedEvent}
                onClose={() => setSelectedEvent(null)}
              />
            )
          }
          showSideSection={!!selectedEvent}
        />
      )}
    </MenuDrivenContainer>
  );
};
